// CAREER panel — the player's own progression: certificates + degrees + majors.
// Distinct from POPULATION (which trains goons). Bonuses computed by profileManager.

import { Events, on, emit } from './eventBus.js'
import {
  LicenseConfig,
  LicenseTypes,
  DegreeSubjects,
  DegreeSubjectLabels,
  DegreeTiers,
  DegreeTierOrder,
  ElectedTitleIds,
  ElectionConfig,
  TitleConfig,
} from '../config/balance.js'
import {
  purchaseLicense,
  getDegreeTier,
  nextTierFor,
  canEarnDegree,
  phdSubjectCount,
  getBonuses,
} from '../game/profileManager.js'
import {
  apply as applyElection,
  resign as resignElection,
  meetsPrereqs,
  getRoster,
  playerHoldsElectedTitle,
  thresholdToClaim,
} from '../game/electionManager.js'
import { getProfile } from '../game/state.js'
import { getWealth } from '../game/seasonManager.js'
import { makeModalToggleable } from './railPanel.js'
import { formatCoins } from './format.js'
import { setHtmlIfChanged } from './domUtil.js'
import { appendBuyCoinsLink } from './buyCoinsLink.js'

export function mountCareerPanel({ root, toggleBtn }) {
  root.innerHTML = `
    <div class="hud__build-header">
      <span class="hud__build-title">CAREER</span>
      <span class="hud__build-tiles" data-summary>—</span>
    </div>
    <div class="hud__pop-section">
      <div class="hud__pop-section-title">LICENSES</div>
      <div data-licenses></div>
    </div>
    <div class="hud__pop-section">
      <div class="hud__pop-section-title">ACADEMY — DEGREES</div>
      <div class="hud__academy-hint">Pass a Three-Kingdoms quiz to earn each degree. Every degree lifts your Honor; every <strong>PhD</strong> permanently adds <strong>+1</strong> to your paw click.</div>
      <div data-degrees></div>
    </div>
    <div class="hud__pop-section">
      <div class="hud__pop-section-title">ELECTION</div>
      <div data-elections></div>
    </div>
  `

  const licensesHost = root.querySelector('[data-licenses]')
  const degreesHost = root.querySelector('[data-degrees]')
  const electionsHost = root.querySelector('[data-elections]')
  const summary = root.querySelector('[data-summary]')

  const rebuild = () => {
    const p = getProfile()
    const owned = p.licenses ?? {}
    const licensesHtml = Object.values(LicenseTypes).map((id) => {
      const cfg = LicenseConfig[id]
      const have = !!owned[id]
      const affordable = getWealth() >= cfg.cost
      return licenseRow(cfg, have, affordable)
    }).join('')
    if (setHtmlIfChanged(licensesHost, licensesHtml)) {
      licensesHost.querySelectorAll('[data-license-buy]').forEach((btn) => {
        btn.addEventListener('click', () => purchaseLicense(btn.dataset.licenseBuy))
      })
    }

    // Only touch the DOM when the rows really changed — a rebuild on every income tick eats real mouse clicks.
    if (setHtmlIfChanged(degreesHost, DegreeSubjects.map((s) => degreeRow(s)).join(''))) {
      degreesHost.querySelectorAll('[data-quiz]').forEach((btn) => {
        btn.addEventListener('click', () => {
          emit(Events.OpenQuiz, { subject: btn.dataset.quiz, tier: btn.dataset.tier })
        })
      })
      degreesHost.querySelectorAll('[data-study]').forEach((btn) => {
        btn.addEventListener('click', () => {
          emit(Events.OpenStudy, { subject: btn.dataset.study })
        })
      })
    }

    rebuildElections()

    const degreeCount = Object.keys(p.degrees ?? {}).length
    const phds = phdSubjectCount()
    const b = getBonuses()
    const parts = []
    if (degreeCount) parts.push(`${degreeCount} degree${degreeCount > 1 ? 's' : ''}`)
    if (phds) parts.push(`+${phds} click`)
    if (b.taxBonus) parts.push(`+${Math.round(b.taxBonus * 100)}% tax`)
    if (b.culturePoints) parts.push(`${b.culturePoints} 文`)
    summary.textContent = parts.length ? parts.join(' · ') : 'no credentials'
    summary.classList.toggle('active', parts.length > 0)
  }

  on(Events.WealthChange, rebuild)
  on(Events.LicensePurchased, rebuild)
  on(Events.DegreePurchased, rebuild)
  on(Events.QuizFinished, rebuild)
  on(Events.ProfileChange, rebuild)
  on(Events.KingdomChange, rebuild)
  on(Events.ElectionWon, rebuild)
  on(Events.ElectionResigned, rebuild)

  function rebuildElections() {
    const held = playerHoldsElectedTitle()
    const wealth = getWealth()
    const electionsHtml = ElectedTitleIds.map((titleId) => {
      const cfg = TitleConfig[titleId]
      const ec = ElectionConfig[titleId]
      const roster = getRoster(getProfile().homeKingdom, titleId)
      const filled = roster.length
      const cap = ec.cap
      const eligible = meetsPrereqs(titleId)
      const threshold = thresholdToClaim(titleId)
      const youHold = held === titleId
      let action
      if (youHold) {
        action = `<button class="hud__build-buy" data-resign>RESIGN</button>`
      } else if (!eligible) {
        action = `<button class="hud__build-buy" disabled>NEED ${reqLabel(ec)}</button>`
      } else if (filled < cap || wealth > threshold) {
        action = `<button class="hud__build-buy" data-apply="${titleId}"><span class="lbl">APPLY</span>${threshold ? `<span class="price">> ${formatCoins(threshold)}</span>` : ''}</button>`
      } else {
        action = `<button class="hud__build-buy" disabled>NEED &gt; ${formatCoins(threshold)}</button>`
      }
      return `
        <div class="hud__build-row">
          <div class="hud__build-info">
            <div class="hud__build-name">${cfg.name} ${cfg.subtitle}</div>
            <div class="hud__build-desc">${filled}/${cap} seats · ${cfg.description ?? ''}</div>
          </div>
          ${action}
        </div>
      `
    }).join('')
    if (!setHtmlIfChanged(electionsHost, electionsHtml)) return
    electionsHost.querySelectorAll('[data-apply]').forEach((btn) => {
      btn.addEventListener('click', () => applyElection(btn.dataset.apply))
    })
    const resignBtn = electionsHost.querySelector('[data-resign]')
    if (resignBtn) resignBtn.addEventListener('click', () => resignElection())
  }

  function reqLabel(ec) {
    const tier = DegreeTiers[ec.minTier]?.name ?? ec.minTier
    if (ec.requiredSubject) return `${DegreeSubjectLabels[ec.requiredSubject]} ${tier}`
    if (ec.requiredSubjectOneOf) {
      return `${ec.requiredSubjectOneOf.map((s) => DegreeSubjectLabels[s]).join('/')} ${tier}`
    }
    return `any ${tier}`
  }

  rebuild()
  appendBuyCoinsLink(root) // quiet link to the Shop, at the foot of the pop-up
  makeModalToggleable(root, toggleBtn)
}

function licenseRow(cfg, have, affordable) {
  return `
    <div class="hud__build-row">
      <div class="hud__build-info">
        <div class="hud__build-name">${cfg.name}</div>
        <div class="hud__build-desc">${cfg.desc}</div>
      </div>
      ${
        have
          ? `<div class="hud__build-owned" style="color:var(--gold)">✓ HELD</div>`
          : `<button class="hud__build-buy" data-license-buy="${cfg.id}" ${affordable ? '' : 'disabled'}>
              <span class="lbl">GET</span>
              <span class="price">${formatCoins(cfg.cost)}</span>
            </button>`
      }
    </div>
  `
}

// One row per subject: shows current tier (dots) + the next quiz to sit, plus a
// foldable study guide. Tiers must be earned in order; PhD is the top.
function degreeRow(subject) {
  const label = DegreeSubjectLabels[subject] ?? subject
  const current = getDegreeTier(subject)          // null | CERT | MASTERS | PHD
  const next = nextTierFor(subject)               // next tier id, or null if PhD held
  const currentIdx = current ? DegreeTierOrder.indexOf(current) : -1
  const dots = DegreeTierOrder
    .map((t, i) => `<span class="hud__degree-dot${i <= currentIdx ? ' on' : ''}" title="${DegreeTiers[t].name}">${i <= currentIdx ? '●' : '○'}</span>`)
    .join('')
  const heldLabel = current ? DegreeTiers[current].name : 'Not enrolled'

  let action
  if (!next) {
    action = `<div class="hud__build-owned" style="color:var(--gold)">🎓 PhD</div>`
  } else {
    const cost = DegreeTiers[next].cost
    const affordable = canEarnDegree(subject, next)
    action = `<button class="hud__build-buy" data-quiz="${subject}" data-tier="${next}" ${affordable ? '' : 'disabled'}>
        <span class="lbl">QUIZ → ${DegreeTiers[next].name}</span>
        <span class="price">${formatCoins(cost)}</span>
      </button>`
  }

  return `
    <div class="hud__build-row hud__degree-row">
      <div class="hud__build-info">
        <div class="hud__build-name">${label} <span class="hud__degree-dots">${dots}</span></div>
        <div class="hud__build-desc">${heldLabel} · <button type="button" class="hud__degree-study" data-study="${subject}">📖 study guide</button></div>
      </div>
      ${action}
    </div>
  `
}
